// Sala Hot Seat con fondo pregenerado: el salón es una imagen fija y por cuadro solo se dibuja un recorte
// de ella (cámara con zoom) más la bola de fuego y sus chispas. Mucho más liviano que dibujar el salón entero.
import roomUrl from "../../../assets/images/hotseat/casino-room.webp";

export const ROOM = { w: 1672, h: 941 };
// Centro de cada máquina (px de la imagen) y el punto del piso donde se detiene la bola, frente a su pantalla.
const CENTERS = [
  [672, 275], [508, 375], [838, 360], [385, 490], [680, 470], [1005, 455],
  [553, 595], [868, 570], [1185, 555], [740, 665], [1055, 655],
];
export const MACHINES = CENTERS.map(([x, y]) => ({ x, y, front: [x - 18, y + 82] }));
// Recorrido de espera: pasillos del salón (curva cerrada).
const WAY = [
  [560, 330], [700, 350], [900, 330], [1060, 400], [1150, 500], [1250, 610], [1100, 720], [900, 740],
  [700, 740], [560, 690], [450, 570], [330, 540], [420, 430], [560, 440], [760, 440], [930, 510], [760, 540], [600, 520],
];

const smooth = (t) => t * t * (3 - 2 * t);
function spline(pts, closed, steps = 14) {
  const n = pts.length;
  const g = (i) => (closed ? pts[((i % n) + n) % n] : pts[Math.max(0, Math.min(n - 1, i))]);
  const out = [];
  for (let i = 0; i < (closed ? n : n - 1); i++) {
    const p0 = g(i - 1), p1 = g(i), p2 = g(i + 1), p3 = g(i + 2);
    for (let k = 0; k < steps; k++) {
      const t = k / steps, t2 = t * t, t3 = t2 * t;
      const c = (a) => 0.5 * (2 * p1[a] + (-p0[a] + p2[a]) * t + (2 * p0[a] - 5 * p1[a] + 4 * p2[a] - p3[a]) * t2 + (-p0[a] + 3 * p1[a] - 3 * p2[a] + p3[a]) * t3);
      out.push([c(0), c(1)]);
    }
  }
  if (!closed) out.push(pts[n - 1]);
  return out;
}
function makeRoute(pts, closed) {
  const line = spline(pts, closed), cum = [0];
  for (let i = 1; i < line.length; i++) cum.push(cum[i - 1] + Math.hypot(line[i][0] - line[i - 1][0], line[i][1] - line[i - 1][1]));
  if (closed) {
    const a = line[line.length - 1], b = line[0];
    line.push(b);
    cum.push(cum[cum.length - 1] + Math.hypot(b[0] - a[0], b[1] - a[1]));
  }
  return { line, cum, L: cum[cum.length - 1] };
}
function posAt(r, d) {
  d = Math.max(0, Math.min(r.L, d));
  let lo = 0, hi = r.cum.length - 1;
  while (hi - lo > 1) { const m = (lo + hi) >> 1; r.cum[m] < d ? lo = m : hi = m; }
  const u = (d - r.cum[lo]) / (r.cum[hi] - r.cum[lo] || 1), A = r.line[lo], B = r.line[hi];
  return [A[0] + (B[0] - A[0]) * u, A[1] + (B[1] - A[1]) * u];
}
const LOOP = makeRoute(WAY, true);
const loopAt = (d) => posAt(LOOP, ((d % LOOP.L) + LOOP.L) % LOOP.L);

const W = 1280, H = 720;
const Z0 = 1.25, ZF = 2.2;        // zoom inicial (px del canvas por px de imagen) y zoom final sobre la ganadora
const SPEED = 330;                // px de imagen por segundo durante el recorrido
const APPROACH_MS = 2500, HOLD_MS = 1100, DIVE_MS = 1400;

/**
 * @param {HTMLCanvasElement} canvas
 * @param {{ minMs?: number, onReady?: () => void, onComplete?: (r: { empty?: boolean }) => void, onPhase?: (phase: string) => void, onError?: (e: unknown) => void }} options
 * @returns {{ setOutcome: (slot: number | null) => void, destroy: () => void }}
 */
export function createRoomFireball(canvas, { minMs = 2500, onReady, onComplete, onPhase, onError } = {}) {
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d");
  let disposed = false, raf = 0, img = null, outcome, phase = "tour", tPhase = 0, tourD = 0, approach = null;
  let completed = false, completedAt = 0, announced = false, zoom = 0, last = 0, lastBall = [...loopAt(0), 46];
  let follow = loopAt(0).slice();
  const parts = [], t0 = performance.now();
  const enter = (name, now) => { phase = name; tPhase = now; onPhase?.(name); };

  const spawn = (x, y, h, n) => {
    for (let i = 0; i < n; i++) {
      const a = Math.random() * 6.283, life = 0.45 + Math.random() * 0.55;
      parts.push({ x: x + Math.cos(a) * 4, y: y - h, vx: Math.cos(a) * 18, vy: -(14 + Math.random() * 40), life, max: life, size: 7 + Math.random() * 9 });
    }
  };
  const sparkle = (x, y, h) => {
    const life = 1 + Math.random() * 0.8;
    parts.push({ x, y: y - h, vx: (Math.random() - 0.5) * 30, vy: -(15 + Math.random() * 35), life, max: life, size: 2 + Math.random() * 2.5, spark: true });
  };
  function drawParts(dt, view) {
    ctx.save();
    ctx.globalCompositeOperation = "lighter";
    for (let i = parts.length - 1; i >= 0; i--) {
      const p = parts[i];
      p.life -= dt;
      if (p.life <= 0) { parts.splice(i, 1); continue; }
      p.x += p.vx * dt;
      p.y += p.vy * dt;
      const t = p.life / p.max, x = (p.x - view.x) * view.z, y = (p.y - view.y) * view.z, r = p.size * view.z * (p.spark ? 1 : 0.4 + t);
      if (x < -r || x > W + r || y < -r || y > H + r) continue;
      if (p.spark) {
        ctx.fillStyle = `rgba(255,225,150,${t * 0.9})`;
        ctx.fillRect(x - r / 2, y - r / 2, r, r);
        continue;
      }
      const g = ctx.createRadialGradient(x, y, 0, x, y, r);
      g.addColorStop(0, `rgba(255,200,90,${0.55 * t})`);
      g.addColorStop(0.5, `rgba(255,110,20,${0.3 * t})`);
      g.addColorStop(1, "rgba(255,60,0,0)");
      ctx.fillStyle = g;
      ctx.fillRect(x - r, y - r, 2 * r, 2 * r);
    }
    ctx.restore();
  }
  function drawBall(b, view, alpha, now) {
    const pulse = 1 + 0.08 * Math.sin(now / 90);
    const gx = (b[0] - view.x) * view.z, gy = (b[1] - view.y) * view.z, hy = gy - b[2] * view.z;
    ctx.save();
    ctx.globalAlpha = alpha;
    ctx.fillStyle = "rgba(0,0,0,.28)";                                  // sombra de la bola
    ctx.beginPath();
    ctx.ellipse(gx, gy, 26 * view.z, 9 * view.z, 0, 0, 6.283);
    ctx.fill();
    ctx.globalCompositeOperation = "lighter";
    let R = 90 * view.z, g = ctx.createRadialGradient(gx, gy, 0, gx, gy, R);   // luz sobre el piso
    g.addColorStop(0, "rgba(255,150,50,.34)");
    g.addColorStop(1, "rgba(255,90,10,0)");
    ctx.fillStyle = g;
    ctx.save();
    ctx.translate(gx, gy);
    ctx.scale(1, 0.55);
    ctx.translate(-gx, -gy);
    ctx.fillRect(gx - R, gy - R, 2 * R, 2 * R);
    ctx.restore();
    R = 46 * view.z * pulse;
    g = ctx.createRadialGradient(gx, hy, 0, gx, hy, R);
    g.addColorStop(0, "rgba(255,250,215,1)");
    g.addColorStop(0.18, "rgba(255,205,100,.95)");
    g.addColorStop(0.5, "rgba(255,110,25,.45)");
    g.addColorStop(1, "rgba(255,60,0,0)");
    ctx.fillStyle = g;
    ctx.fillRect(gx - R, hy - R, 2 * R, 2 * R);
    ctx.restore();
  }

  function frame(now) {
    if (disposed) return;
    raf = requestAnimationFrame(frame);
    const dt = Math.min(0.05, (now - last) / 1000);
    last = now;
    const elapsed = now - t0;
    let b = lastBall, ballA = 1, zoomTarget = 0;
    if (phase === "tour") {
      tourD += dt * SPEED;
      const p = loopAt(tourD);
      b = [p[0], p[1], 46 + 7 * Math.sin(elapsed / 250)];
      if (outcome !== undefined && elapsed >= minMs) {
        if (outcome.machine) {
          const cur = loopAt(tourD), prev = loopAt(tourD - 40), f = outcome.machine.front;
          const mid = [(cur[0] + f[0]) / 2, (cur[1] + f[1]) / 2];
          const route = makeRoute([prev, cur, mid, f], false);
          approach = { route, d0: route.cum[Math.min(route.cum.length - 1, 14)], machine: outcome.machine };
          enter("approach", now);
        } else enter("empty", now);
      }
    }
    if (phase === "approach") {
      const u = Math.min(1, (now - tPhase) / APPROACH_MS), s = 1 - Math.pow(1 - u, 2.2);
      const p = posAt(approach.route, approach.d0 + s * (approach.route.L - approach.d0));
      b = [p[0], p[1], 46 + 7 * Math.sin(elapsed / 250)];
      if (u > 0.6) zoomTarget = Math.min(0.5, ((u - 0.6) / 0.4) * 0.5);   // el zoom empieza antes de que la bola se detenga
      if (u >= 1) enter("zoom", now);
    } else if (phase === "zoom") {
      b = [approach.machine.front[0], approach.machine.front[1], 38];
      ballA = Math.max(0, 1 - (now - tPhase) / 700);
      zoomTarget = 1;
      if (!completed && zoom > 0.97 && now - tPhase > HOLD_MS) { completed = true; completedAt = now; onComplete?.({ empty: false }); }
    } else if (phase === "empty") {
      ballA = Math.max(0, 1 - (now - tPhase) / 700);
      if (!completed && now - tPhase > 900) { completed = true; completedAt = now; onComplete?.({ empty: true }); }
    }
    lastBall = b;
    if (phase === "tour" || phase === "approach") {                 // la cámara sigue a la bola
      const fk = 1 - Math.exp(-dt * 1.6);
      follow = [follow[0] + (b[0] - follow[0]) * fk, follow[1] + (b[1] - 40 - follow[1]) * fk];
    }
    zoom += (zoomTarget - zoom) * Math.min(1, dt * 3);
    const e = smooth(zoom), tg = approach ? [approach.machine.x, approach.machine.y + 20] : follow;
    let z = Z0 + (ZF - Z0) * e;
    if (completedAt) z *= 1 + 0.35 * smooth(Math.min(1, (now - completedAt) / DIVE_MS));   // sigue acercándose mientras la página se funde
    const vw = W / z, vh = H / z;                                   // la cámara nunca sale de la imagen
    const cx = Math.max(vw / 2, Math.min(ROOM.w - vw / 2, follow[0] + (tg[0] - follow[0]) * e));
    const cy = Math.max(vh / 2, Math.min(ROOM.h - vh / 2, follow[1] + (tg[1] - follow[1]) * e));
    const view = { x: cx - vw / 2, y: cy - vh / 2, z };

    ctx.fillStyle = "#07050a";
    ctx.fillRect(0, 0, W, H);
    ctx.drawImage(img, view.x, view.y, vw, vh, 0, 0, W, H);
    if (phase === "tour" || phase === "approach") {
      spawn(b[0], b[1], b[2], 3);
      sparkle(b[0], b[1], b[2]);
      sparkle(b[0], b[1], b[2]);
    }
    drawParts(dt, view);
    if (ballA > 0) drawBall(b, view, ballA, now);
    if (!announced) { announced = true; onPhase?.(phase); onReady?.(); }
  }

  const image = new Image();
  image.onload = () => {
    if (disposed) return;
    img = image;
    last = performance.now();
    raf = requestAnimationFrame(frame);
  };
  image.onerror = (error) => { if (!disposed) onError?.(error); };
  image.src = roomUrl;

  return {
    /** slot: 0..19 (posición visual de la ganadora) o null si no hay ganador */
    setOutcome(slot) {
      outcome = slot === null ? { machine: null } : { machine: MACHINES[(slot * 3 + 1) % MACHINES.length] };
    },
    destroy() {
      disposed = true;
      cancelAnimationFrame(raf);
    },
  };
}
